import React, { FC, Suspense } from 'react';
import { Link, Outlet, useLocation, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Breadcrumbs } from '@digdir/designsystemet-react';
import { useTranslation } from 'react-i18next';

import styled from '../../styled';
import { SessionExpiresDialog } from '../../components/sessionExpiresDialog';
import BuildingDetailsProvider from '../../hooks/useBuildingDetails/provider';
import { BuildingId } from '../../utils/navigation';
import { useBuildingData } from '../../hooks/useBuildingDetails/buildingDetailsHooks';
import {
  BreadcrumbsWrapper,
  MainContentWrapper,
  SidebarPageLayout,
  SidebarWrapper
} from '../registrationV2025/components/styledComponents';
import { PageContentLoader, SidebarPageLoader } from '../registrationV2025/components/pageLoader';

import { useAddressBuildingTitle } from './utils';
import { BuildingMenu } from './menu';

export const BuildingPageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--ds-size-8);
`;

const isBalderDesign = window.config.VITE_BALDER_DESIGN_ENABLED;

const BuildingPageLayout: FC = () => {
  const { buildingId } = useParams<BuildingId>();
  const { building, buildingLoading } = useBuildingData(buildingId);
  const { pathname } = useLocation();

  const { t } = useTranslation();
  const tab = pathname.split('/').pop() ?? '';
  const helmetTitle = useAddressBuildingTitle(tab, building);

  if (buildingLoading) {
    return <SidebarPageLoader />;
  }

  if (!building) {
    return null;
  }
  if (!isBalderDesign) {
    // TODO: Remove when feature flag is removed. This is a temporary fix for centering the content on the page.
    return (
      <div style={{ margin: '0 auto' }}>
        <BuildingDetailsProvider>
          <Helmet title={helmetTitle} />
          <SidebarPageLayout>
            <BreadcrumbsWrapper>
              <Breadcrumbs>
                <Breadcrumbs.Link asChild aria-label={t('back')}>
                  <Link to="/">{t('back')}</Link>
                </Breadcrumbs.Link>
              </Breadcrumbs>
            </BreadcrumbsWrapper>
            <SidebarWrapper>
              <BuildingMenu building={building} />
            </SidebarWrapper>
            <MainContentWrapper>
              <Suspense fallback={<PageContentLoader />}>
                <Outlet />
              </Suspense>
            </MainContentWrapper>
          </SidebarPageLayout>
          <SessionExpiresDialog />
        </BuildingDetailsProvider>
      </div>
    );
  }

  return (
    <BuildingDetailsProvider>
      <Helmet title={helmetTitle} />
      <SidebarPageLayout>
        <BreadcrumbsWrapper>
          <Breadcrumbs>
            <Breadcrumbs.Link asChild aria-label={t('back')}>
              <Link to="/">{t('back')}</Link>
            </Breadcrumbs.Link>
          </Breadcrumbs>
        </BreadcrumbsWrapper>
        <SidebarWrapper>
          <BuildingMenu building={building} />
        </SidebarWrapper>
        <MainContentWrapper>
          <Suspense fallback={<PageContentLoader />}>
            <Outlet />
          </Suspense>
        </MainContentWrapper>
      </SidebarPageLayout>
      <SessionExpiresDialog />
    </BuildingDetailsProvider>
  );
};

export default BuildingPageLayout;
